import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import ManualSelectionsBanner from '../components/ManualSelectionsBanner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { useAuth } from '../contexts/AuthContext';
import {
  Loader2,
  Upload,
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Eye,
  RefreshCw,
  Send,
  Download,
  Edit2,
  Search,
  Filter,
  ChevronRight,
  Building2,
  User,
  Calendar,
  FileCheck,
  Sparkles,
  X,
  Plus,
  Trash2,
  ExternalLink,
  File,
  FileType,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  uploaded: { label: 'Uploaded', color: 'bg-slate-100 text-slate-700', icon: Upload },
  parsed: { label: 'Parsed', color: 'bg-blue-100 text-blue-700', icon: FileCheck },
  generated: { label: 'Generated', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  sent: { label: 'Sent', color: 'bg-purple-100 text-purple-700', icon: Send },
  parse_failed: { label: 'Parse Failed', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

export default function DossierGenerationPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const fileInputRef = useRef(null);
  
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [requirements, setRequirements] = useState([]);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [requirementFilter, setRequirementFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadRequirement, setUploadRequirement] = useState('none');
  
  // Detail drawer
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedParsedJson, setEditedParsedJson] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  
  // Drag and drop
  const [isDragging, setIsDragging] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('ats_token');

  const loadDossiers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), page_size: '20' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (requirementFilter !== 'all') params.append('requirement_id', requirementFilter);
      if (search) params.append('search', search);
      
      const response = await fetch(`${API_URL}/api/manual-selections/dossiers?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDossiers(data.dossiers);
        setTotalPages(data.total_pages);
      }
    } catch (error) {
      console.error('Failed to load dossiers:', error);
      toast.error('Failed to load dossiers');
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, page, statusFilter, requirementFilter, search]);

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/dossiers/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadRequirements = async () => {
    try {
      const response = await fetch(`${API_URL}/api/requirements?page_size=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequirements(data.requirements || []);
      }
    } catch (error) {
      console.error('Failed to load requirements:', error);
    }
  };

  useEffect(() => {
    loadDossiers();
    loadStats();
    loadRequirements();
  }, [loadDossiers]);

  // File upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return ['pdf', 'doc', 'docx', 'txt'].includes(ext);
    });
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    const validFiles = files.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return ['pdf', 'doc', 'docx', 'txt'].includes(ext);
    });
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file');
      return;
    }
    
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (uploadRequirement !== 'none') {
          formData.append('requirement_id', uploadRequirement);
        }
        
        const response = await fetch(`${API_URL}/api/manual-selections/dossiers/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }
    
    setUploading(false);
    setSelectedFiles([]);
    setUploadRequirement('none');
    setUploadModalOpen(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded successfully`);
      loadDossiers();
      loadStats();
    }
    if (failCount > 0) {
      toast.error(`${failCount} file(s) failed to upload`);
    }
  };

  // Detail drawer actions
  const openDetailDrawer = async (dossier) => {
    setSelectedDossier(dossier);
    setDetailDrawerOpen(true);
    setDetailLoading(true);
    setEditing(false);
    
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/dossiers/${dossier.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedDossier(data);
        setEditedParsedJson(data.parsed_json || {});
      }
    } catch (error) {
      console.error('Failed to load dossier detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const saveEditedParsedJson = async () => {
    if (!selectedDossier) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/dossiers/${selectedDossier.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ parsed_json: editedParsedJson })
      });
      
      if (response.ok) {
        toast.success('Dossier updated');
        setEditing(false);
        setSelectedDossier(prev => ({ ...prev, parsed_json: editedParsedJson }));
        loadDossiers();
      } else {
        toast.error('Failed to save changes');
      }
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const generatePdf = async () => {
    if (!selectedDossier) return;
    
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/dossiers/${selectedDossier.id}/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`PDF generated: ${data.resume_number}`);
        
        // Refresh detail
        openDetailDrawer(selectedDossier);
        loadDossiers();
        loadStats();
        
        // Open preview in new tab
        if (data.preview_url) {
          window.open(data.preview_url, '_blank');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate PDF');
      }
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  const regeneratePdf = async () => {
    if (!selectedDossier) return;
    
    setRegenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/dossiers/${selectedDossier.id}/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`PDF regenerated (v${data.version})`);
        openDetailDrawer(selectedDossier);
        loadDossiers();
        
        if (data.preview_url) {
          window.open(data.preview_url, '_blank');
        }
      } else {
        toast.error('Failed to regenerate PDF');
      }
    } catch (error) {
      toast.error('Failed to regenerate PDF');
    } finally {
      setRegenerating(false);
    }
  };

  const reparse = async () => {
    if (!selectedDossier) return;
    
    setReparsing(true);
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/dossiers/${selectedDossier.id}/parse`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Resume re-parsed successfully');
        setEditedParsedJson(data.parsed_json);
        setSelectedDossier(prev => ({ ...prev, parsed_json: data.parsed_json, status: 'parsed' }));
        loadDossiers();
        loadStats();
      } else {
        toast.error('Failed to re-parse');
      }
    } catch (error) {
      toast.error('Failed to re-parse');
    } finally {
      setReparsing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy, HH:mm');
    } catch {
      return dateStr;
    }
  };

  const formatDateIST = (dateStr) => {
    if (!dateStr) return '-';
    try {
      // Parse and display in IST
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl flex items-center justify-center shadow-lg">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="dossier-page-title">
                Dossier Generation
              </h1>
              <p className="text-slate-500 mt-1">
                Generate branded candidate dossiers for clients
              </p>
            </div>
          </div>
          
          <Button
            onClick={() => setUploadModalOpen(true)}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Resumes
          </Button>
        </div>

        {/* Manual Selections Banner */}
        <ManualSelectionsBanner />

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.pending_action}</p>
                  <p className="text-xs text-slate-500">Pending Action</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.parsed}</p>
                  <p className="text-xs text-slate-500">Parsed</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.generated}</p>
                  <p className="text-xs text-slate-500">Generated</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.sent}</p>
                  <p className="text-xs text-slate-500">Sent</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.parse_failed}</p>
                  <p className="text-xs text-slate-500">Failed</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name or resume number..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="uploaded">Uploaded</SelectItem>
                  <SelectItem value="parsed">Parsed</SelectItem>
                  <SelectItem value="generated">Generated</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="parse_failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={requirementFilter} onValueChange={(v) => { setRequirementFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[200px]" data-testid="requirement-filter">
                  <SelectValue placeholder="Requirement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Requirements</SelectItem>
                  {requirements.map((req) => (
                    <SelectItem key={req.id} value={req.id}>{req.position_title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={() => { loadDossiers(); loadStats(); }}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Dossiers List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : dossiers.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No dossiers yet</h3>
                <p className="text-slate-500 mb-4">Upload resumes to generate branded dossiers</p>
                <Button onClick={() => setUploadModalOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Resumes
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Candidate</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Resume #</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Requirement</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Created By</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Created</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dossiers.map((dossier) => {
                      const statusConf = STATUS_CONFIG[dossier.status] || STATUS_CONFIG.uploaded;
                      const StatusIcon = statusConf.icon;
                      
                      return (
                        <tr 
                          key={dossier.id} 
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => openDetailDrawer(dossier)}
                          data-testid={`dossier-row-${dossier.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-indigo-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">
                                  {dossier.candidate_name || dossier.original_filename || 'Unknown'}
                                </p>
                                {dossier.parsed_json?.current_title && (
                                  <p className="text-xs text-slate-500">{dossier.parsed_json.current_title}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {dossier.resume_number ? (
                              <span className="font-mono text-sm text-indigo-600 font-medium">{dossier.resume_number}</span>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`${statusConf.color} gap-1`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConf.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {dossier.requirement_title ? (
                              <div>
                                <p className="text-sm text-slate-700">{dossier.requirement_title}</p>
                                {dossier.client_name && (
                                  <p className="text-xs text-slate-500 flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />
                                    {dossier.client_name}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-600">{dossier.creator_name || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-500">{formatDateIST(dossier.created_at)}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                              {dossier.status === 'generated' && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); openDetailDrawer(dossier); }}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Preview
                                </Button>
                              )}
                              {dossier.status === 'parsed' && (
                                <Button 
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={(e) => { e.stopPropagation(); openDetailDrawer(dossier); }}
                                >
                                  <Sparkles className="w-4 h-4 mr-1" />
                                  Generate
                                </Button>
                              )}
                              {dossier.status === 'parse_failed' && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); openDetailDrawer(dossier); }}
                                >
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Retry
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-600" />
              Upload Resumes
            </DialogTitle>
            <DialogDescription>
              Drag and drop or select resume files to generate branded dossiers.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Drag & Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Upload className="w-10 h-10 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600 mb-2">Drag & drop files here</p>
              <p className="text-sm text-slate-400 mb-4">PDF, DOC, DOCX, TXT (max 10MB each)</p>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                Select Files
              </Button>
            </div>
            
            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Files ({selectedFiles.length})</Label>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <File className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-700 truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(idx)}>
                        <X className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Requirement Selection */}
            <div>
              <Label>Link to Requirement (Optional)</Label>
              <Select value={uploadRequirement} onValueChange={setUploadRequirement}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select requirement..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No requirement</SelectItem>
                  {requirements.map((req) => (
                    <SelectItem key={req.id} value={req.id}>{req.position_title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={uploadFiles} 
              disabled={uploading || selectedFiles.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Upload {selectedFiles.length > 0 && `(${selectedFiles.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Drawer */}
      <Sheet open={detailDrawerOpen} onOpenChange={setDetailDrawerOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Dossier Details
            </SheetTitle>
          </SheetHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : selectedDossier && (
            <div className="mt-6 space-y-6">
              {/* Status & Resume Number */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedDossier.resume_number && (
                    <Badge className="font-mono bg-indigo-100 text-indigo-700 text-base px-3 py-1">
                      {selectedDossier.resume_number}
                    </Badge>
                  )}
                  <Badge className={STATUS_CONFIG[selectedDossier.status]?.color || 'bg-slate-100'}>
                    {STATUS_CONFIG[selectedDossier.status]?.label || selectedDossier.status}
                  </Badge>
                </div>
                {selectedDossier.version > 1 && (
                  <span className="text-sm text-slate-500">Version {selectedDossier.version}</span>
                )}
              </div>
              
              {/* File Links */}
              <div className="flex gap-3">
                {selectedDossier.original_url && (
                  <Button variant="outline" size="sm" onClick={() => window.open(selectedDossier.original_url, '_blank')}>
                    <Download className="w-4 h-4 mr-1" />
                    Original File
                  </Button>
                )}
                {selectedDossier.generated_url && (
                  <Button variant="outline" size="sm" onClick={() => window.open(selectedDossier.generated_url, '_blank')}>
                    <FileType className="w-4 h-4 mr-1" />
                    Generated PDF
                  </Button>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                {(selectedDossier.status === 'uploaded' || selectedDossier.status === 'parse_failed') && (
                  <Button onClick={reparse} disabled={reparsing}>
                    {reparsing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <RefreshCw className="w-4 h-4 mr-1" />
                    {selectedDossier.status === 'parse_failed' ? 'Retry Parse' : 'Parse Now'}
                  </Button>
                )}
                {selectedDossier.status === 'parsed' && (
                  <Button onClick={generatePdf} disabled={generating} className="bg-green-600 hover:bg-green-700">
                    {generating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <Sparkles className="w-4 h-4 mr-1" />
                    Generate PDF
                  </Button>
                )}
                {selectedDossier.status === 'generated' && (
                  <Button onClick={regeneratePdf} disabled={regenerating} variant="outline">
                    {regenerating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Regenerate
                  </Button>
                )}
                {selectedDossier.parsed_json && (
                  <Button
                    variant={editing ? "default" : "outline"}
                    onClick={() => setEditing(!editing)}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    {editing ? 'Cancel Edit' : 'Edit Fields'}
                  </Button>
                )}
              </div>
              
              {/* Parsed Data Display/Edit */}
              {selectedDossier.parsed_json && (
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold text-slate-900">Parsed Information</h3>
                  
                  {editing ? (
                    // Editable Form
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Full Name</Label>
                          <Input
                            value={editedParsedJson?.full_name || ''}
                            onChange={(e) => setEditedParsedJson(prev => ({ ...prev, full_name: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            value={editedParsedJson?.email || ''}
                            onChange={(e) => setEditedParsedJson(prev => ({ ...prev, email: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input
                            value={editedParsedJson?.phone || ''}
                            onChange={(e) => setEditedParsedJson(prev => ({ ...prev, phone: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Location</Label>
                          <Input
                            value={editedParsedJson?.location || ''}
                            onChange={(e) => setEditedParsedJson(prev => ({ ...prev, location: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Current Title</Label>
                          <Input
                            value={editedParsedJson?.current_title || ''}
                            onChange={(e) => setEditedParsedJson(prev => ({ ...prev, current_title: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Current Company</Label>
                          <Input
                            value={editedParsedJson?.current_company || ''}
                            onChange={(e) => setEditedParsedJson(prev => ({ ...prev, current_company: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Total Experience (Years)</Label>
                          <Input
                            type="number"
                            value={editedParsedJson?.total_experience_years || ''}
                            onChange={(e) => setEditedParsedJson(prev => ({ ...prev, total_experience_years: parseFloat(e.target.value) || null }))}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Summary</Label>
                        <Textarea
                          value={editedParsedJson?.summary || ''}
                          onChange={(e) => setEditedParsedJson(prev => ({ ...prev, summary: e.target.value }))}
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label>Skills (comma-separated)</Label>
                        <Textarea
                          value={(editedParsedJson?.skills || []).join(', ')}
                          onChange={(e) => setEditedParsedJson(prev => ({ ...prev, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                          rows={2}
                        />
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                        <Button onClick={saveEditedParsedJson} disabled={saving}>
                          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Read-only Display
                    <div className="space-y-4 bg-slate-50 p-4 rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">Full Name</p>
                          <p className="font-medium text-slate-900">{selectedDossier.parsed_json.full_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <p className="text-slate-700">{selectedDossier.parsed_json.email || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Phone</p>
                          <p className="text-slate-700">{selectedDossier.parsed_json.phone || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Location</p>
                          <p className="text-slate-700">{selectedDossier.parsed_json.location || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Current Title</p>
                          <p className="text-slate-700">{selectedDossier.parsed_json.current_title || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Current Company</p>
                          <p className="text-slate-700">{selectedDossier.parsed_json.current_company || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Experience</p>
                          <p className="text-slate-700">{selectedDossier.parsed_json.total_experience_years ? `${selectedDossier.parsed_json.total_experience_years} years` : '-'}</p>
                        </div>
                      </div>
                      
                      {selectedDossier.parsed_json.summary && (
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Summary</p>
                          <p className="text-sm text-slate-700">{selectedDossier.parsed_json.summary}</p>
                        </div>
                      )}
                      
                      {selectedDossier.parsed_json.skills?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 mb-2">Skills</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedDossier.parsed_json.skills.slice(0, 20).map((skill, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">{skill}</Badge>
                            ))}
                            {selectedDossier.parsed_json.skills.length > 20 && (
                              <Badge variant="outline" className="text-xs">+{selectedDossier.parsed_json.skills.length - 20} more</Badge>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {selectedDossier.parsed_json.experience?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 mb-2">Experience ({selectedDossier.parsed_json.experience.length})</p>
                          <div className="space-y-2">
                            {selectedDossier.parsed_json.experience.slice(0, 3).map((exp, idx) => (
                              <div key={idx} className="bg-white p-2 rounded border">
                                <p className="font-medium text-sm">{exp.title}</p>
                                <p className="text-xs text-slate-500">{exp.company} • {exp.from} - {exp.to}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {selectedDossier.parsed_json.education?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 mb-2">Education</p>
                          <div className="space-y-1">
                            {selectedDossier.parsed_json.education.map((edu, idx) => (
                              <p key={idx} className="text-sm">{edu.degree} - {edu.institution} {edu.year && `(${edu.year})`}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Metadata */}
              <div className="border-t pt-4 text-sm text-slate-500">
                <p>Created: {formatDateIST(selectedDossier.created_at)}</p>
                {selectedDossier.generated_at && <p>Generated: {formatDateIST(selectedDossier.generated_at)}</p>}
                {selectedDossier.creator?.name && <p>Created by: {selectedDossier.creator.name}</p>}
                {selectedDossier.requirement && (
                  <p>Requirement: {selectedDossier.requirement.position_title} {selectedDossier.requirement.client_name && `@ ${selectedDossier.requirement.client_name}`}</p>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
